import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, ArrowUpOnSquareIcon, PlusIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline';

const InstallPrompt = () => {
  const [show, setShow] = useState(false);
  const [platform, setPlatform] = useState(null); // 'ios' | 'android' | null
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // Don't show if already installed as standalone
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    if (isStandalone) return;

    // Don't show if already dismissed
    const dismissed = localStorage.getItem('patungin_install_dismissed');
    if (dismissed) return;

    // Detect platform
    const ua = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isAndroid = /Android/.test(ua);

    if (isIOS) {
      setPlatform('ios');
      // Small delay so dashboard loads first
      setTimeout(() => setShow(true), 1500);
    } else if (isAndroid) {
      setPlatform('android');
      // Listen for Chrome's beforeinstallprompt
      const handler = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setTimeout(() => setShow(true), 1500);
      };
      window.addEventListener('beforeinstallprompt', handler);
      // Fallback: show manual instructions after 2s if no prompt event
      const fallbackTimer = setTimeout(() => {
        if (!deferredPrompt) {
          setTimeout(() => setShow(true), 1500);
        }
      }, 2000);
      return () => {
        window.removeEventListener('beforeinstallprompt', handler);
        clearTimeout(fallbackTimer);
      };
    } else {
      // Desktop or other - show generic Chrome instructions
      const handler = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setTimeout(() => setShow(true), 1500);
      };
      window.addEventListener('beforeinstallprompt', handler);
      return () => window.removeEventListener('beforeinstallprompt', handler);
    }
  }, []);

  const handleDismiss = () => {
    setShow(false);
    localStorage.setItem('patungin_install_dismissed', 'true');
  };

  const handleInstallChrome = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        localStorage.setItem('patungin_install_dismissed', 'true');
      }
      setDeferredPrompt(null);
      setShow(false);
    }
  };

  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/40 z-[70] flex items-end sm:items-center justify-center"
        onClick={handleDismiss}
      >
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="bg-white rounded-t-[32px] sm:rounded-3xl p-6 w-full max-w-md shadow-[0_-10px_40px_rgba(0,0,0,0.1)]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="w-10 h-1 bg-slate-300 rounded-full mx-auto mb-6 sm:hidden"></div>

          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-bold text-slate-900">Install Patungin</h3>
            <button
              onClick={handleDismiss}
              className="w-8 h-8 rounded-full bg-surface flex items-center justify-center hover:bg-surface-hover transition-colors"
            >
              <XMarkIcon className="w-5 h-5 text-slate-600" />
            </button>
          </div>

          {/* App Preview */}
          <div className="flex items-center gap-4 bg-surface p-4 rounded-2xl mb-6">
            <img src="/icon-192x192.png" alt="Patungin" className="w-14 h-14 rounded-2xl shadow-sm" />
            <div>
              <h4 className="font-bold text-slate-900">Patungin</h4>
              <p className="text-xs text-slate-500">Patungan gak pake pusing</p>
            </div>
          </div>

          {/* Instructions */}
          {platform === 'ios' ? (
            <div className="space-y-4 mb-6">
              <p className="text-sm text-slate-600 leading-relaxed">
                Tambahkan Patungin ke Home Screen supaya bisa diakses seperti aplikasi:
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-2xl">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <ArrowUpOnSquareIcon className="w-5 h-5 text-blue-600" />
                  </div>
                  <p className="text-sm text-slate-700">
                    <span className="font-semibold">1.</span> Ketuk tombol <span className="font-semibold text-blue-600">Share</span> <ArrowUpOnSquareIcon className="w-4 h-4 inline text-blue-600" /> di bawah Safari
                  </p>
                </div>
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-2xl">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <PlusIcon className="w-5 h-5 text-blue-600" />
                  </div>
                  <p className="text-sm text-slate-700">
                    <span className="font-semibold">2.</span> Pilih <span className="font-semibold text-blue-600">"Add to Home Screen"</span>
                  </p>
                </div>
              </div>
            </div>
          ) : deferredPrompt ? (
            <div className="space-y-4 mb-6">
              <p className="text-sm text-slate-600 leading-relaxed">
                Install Patungin di perangkatmu supaya bisa diakses langsung dari Home Screen — tanpa perlu buka browser!
              </p>
              <button
                onClick={handleInstallChrome}
                className="w-full bg-primary text-white py-3.5 rounded-full font-semibold hover:bg-primary-hover active:scale-95 transition-all shadow-float flex items-center justify-center gap-2"
              >
                <PlusIcon className="w-5 h-5" />
                Install Sekarang
              </button>
            </div>
          ) : (
            <div className="space-y-4 mb-6">
              <p className="text-sm text-slate-600 leading-relaxed">
                Tambahkan Patungin ke Home Screen supaya bisa diakses seperti aplikasi:
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-2xl">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <EllipsisVerticalIcon className="w-5 h-5 text-blue-600" />
                  </div>
                  <p className="text-sm text-slate-700">
                    <span className="font-semibold">1.</span> Ketuk tombol <span className="font-semibold text-blue-600">Menu</span> <EllipsisVerticalIcon className="w-4 h-4 inline text-blue-600" /> di Chrome
                  </p>
                </div>
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-2xl">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <PlusIcon className="w-5 h-5 text-blue-600" />
                  </div>
                  <p className="text-sm text-slate-700">
                    <span className="font-semibold">2.</span> Pilih <span className="font-semibold text-blue-600">"Add to Home Screen"</span>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Dismiss */}
          <button
            onClick={handleDismiss}
            className="w-full text-center text-sm text-slate-500 hover:text-slate-700 font-medium py-2 transition-colors"
          >
            Nanti saja
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default InstallPrompt;
